import { Box, Tooltip } from "@mui/material";
import { NBA_TEAMS, GLEAGUE_TEAMS } from "../data/teams";
import { CONDENSED_FONT_FAMILY } from "./constants";

/**
 * "Preliminary" (Trevor 2026-10-26): a report built while a game is still on CTG's same-night (delayed) feed. The
 * backend names the feed (`header.feed` on a game report, `header.delayedFeedGames` on season-type reports); the UI
 * only ever says "preliminary". The flag clears itself when the processed rebuild lands.
 */
export const PRELIMINARY_TOOLTIP =
  "Built from CTG's same-night data. Their corrected postgame data usually arrives within a couple of hours and " +
  "this report updates automatically. Shot quality, xPPP and some play tags may shift.";

export type ReportFeed = "delayed" | "processed";

export interface DelayedFeedGame {
  gameId: string;
  /** YYYY-MM-DD */
  date: string;
  /** NBA team id */
  opponentId: number;
  isHome: boolean;
}

// The date stays a string: dayjs would shift a bare YYYY-MM-DD across time zones
const monthDay = (date: string) => {
  const [, m, d] = date.split("-").map(Number);
  return m && d ? `${m}/${d}` : date;
};

const opponentAbbrev = (nbaId: number) =>
  [...NBA_TEAMS, ...GLEAGUE_TEAMS].find((t) => t.nbaId === nbaId)?.abbrev ?? "OPP";

/** "Includes 10/26 @ UTA on preliminary data"; several games join with ", ". Empty for none. */
export function preliminaryGamesNote(games?: DelayedFeedGame[] | null): string {
  if (!games?.length) return "";
  const list = [...games]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((g) => `${monthDay(g.date)} ${g.isHome ? "vs" : "@"} ${opponentAbbrev(g.opponentId)}`)
    .join(", ");
  return `Includes ${list} on preliminary data`;
}

/** The game report's "Preliminary" tag beside its as-of stamp. Dashed outline in the page's ink: it prints, and it
 * reads as "not final" without borrowing the red / green data colors. */
export function PreliminaryTag({ size = "md" }: { size?: "sm" | "md" }) {
  return (
    <Tooltip title={PRELIMINARY_TOOLTIP} arrow placement="bottom-start" enterDelay={200}>
      <Box
        component="span"
        tabIndex={0}
        aria-label={`Preliminary: ${PRELIMINARY_TOOLTIP}`}
        className="prelim-tag"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          verticalAlign: "middle",
          ml: 0.75,
          px: "5px",
          border: "1px dashed currentColor",
          borderRadius: "2px",
          color: "#1a1a1a",
          fontFamily: CONDENSED_FONT_FAMILY,
          fontSize: size === "sm" ? "0.55rem" : "0.62rem",
          fontWeight: 700,
          letterSpacing: "0.1em",
          lineHeight: 1.5,
          textTransform: "uppercase",
          whiteSpace: "nowrap",
          cursor: "help",
          "&:focus-visible": { outline: "2px solid #1a1a1a", outlineOffset: "1px" },
        }}
      >
        Preliminary
      </Box>
    </Tooltip>
  );
}

/** The season report's line naming the games still on preliminary data. */
export function PreliminaryGamesNote({ games }: { games?: DelayedFeedGame[] | null }) {
  const note = preliminaryGamesNote(games);
  if (!note) return null;
  return (
    <Tooltip title={PRELIMINARY_TOOLTIP} arrow placement="bottom-start" enterDelay={200}>
      <Box
        component="span"
        tabIndex={0}
        className="prelim-note"
        sx={{
          display: "inline-block",
          fontFamily: CONDENSED_FONT_FAMILY,
          fontSize: "11.5px",
          letterSpacing: "0.04em",
          color: "#3a3a3a",
          borderBottom: "1px dashed #8a8a8a",
          lineHeight: 1.3,
          cursor: "help",
          "&:focus-visible": { outline: "2px solid #1a1a1a", outlineOffset: "1px" },
        }}
      >
        {note}
      </Box>
    </Tooltip>
  );
}
